import axios from "axios";
import { createContext, FC, useCallback, useContext, useState } from "react";
import { AuthContext } from "../../AuthContext";
import { useLoading } from "../../LoadingContext";
const qs = require("qs");

type AdminListQuery = {
  page: number;
  size: number;
  username: string;
};

type AdminListServiceContextProps = {
  query: AdminListQuery;
  collection: any[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  roles: { _id: string; roleName: string }[];
  fetchRoles: Function;
  onFetch: Function;
  onFetchV2: (data: any) => Promise<void>;
  setCollection: (collection: any[]) => void;
  updateAdminRole: (id: string, roleId: string) => Promise<void>;
};

const defaultContext: AdminListServiceContextProps = {
  query: {
    page: 1,
    size: 10,
    username: "",
  },
  collection: [],
  hasNextPage: false,
  hasPreviousPage: false,
  roles: [],
  fetchRoles: () => {},
  onFetch: () => {},
  onFetchV2: async () => {},
  setCollection: () => {},
  updateAdminRole: async () => {},
};

const getServiceEndpoint = () =>
  `${process.env.REACT_APP_API_URL}/api/v1/admin/list`;

const getServiceEndpointV2 = () =>
  `${process.env.REACT_APP_API_URL}/api/v2/admin/list`;

const getRolesEndpoint = () =>
  `${process.env.REACT_APP_API_URL}/api/v1/role/list`;

export const AdminListServiceContext = createContext(defaultContext);

export const AdminListServiceContextProvider: FC<any> = ({ children }) => {
  const [collection, setCollection] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [page, setPage] = useState<{
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }>({
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [query] = useState<AdminListQuery>(defaultContext.query);
  const authContext = useContext(AuthContext);
  const loading = useLoading();

  const onFetch = useCallback(
    async (data: AdminListQuery) => {
      loading.push();
      try {
        const response = await axios.post(getServiceEndpoint(), data || query, {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        });

        if (response.data) {
          setCollection(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching collection:", error);
      } finally {
        loading.pop();
      }
    },
    [query, authContext, loading],
  );

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.post(
        getRolesEndpoint(),
        {},
        {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        },
      );
      const roles = response.data.data.map((role: any) => ({
        _id: role._id,
        roleName: role.roleName,
      }));
      setRoles(roles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, [authContext.token]);

  const updateAdminRole = useCallback(
    async (id: string, roleId: string) => {
      try {
        const response = await axios.put(
          `${getServiceEndpoint()}/${id}/role`,
          { roleId },
          {
            headers: {
              Authorization: `Bearer ${authContext.token}`,
            },
          },
        );
      } catch (error) {
        console.error("Error updating admin role:", error);
      }
    },
    [authContext.token],
  );

  const onFetchV2 = useCallback(
    async (data: any) => {
      loading.push();
      try {
        const response = await axios.get(getServiceEndpointV2(), {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
          params: data || query,
          paramsSerializer: (params) => {
            return qs.stringify(params, {
              encode: false,
              arrayFormat: "indices",
              indices: true,
              strictNullHandling: true,
            });
          },
        });
        console.log("Response data:", response.data);
        if (response.data) {
          console.log("Fetched collection v2:", response.data.data.records);
          setCollection(response.data.data.records);
          setPage({
            hasNextPage: response.data.data.hasAfter,
            hasPrevPage: response.data.data.hasBefore,
          });
        }
      } catch (error) {
        console.error("Error fetching v2 collection:", error);
      } finally {
        loading.pop();
      }
    },
    [query, authContext, loading],
  );

  return (
    <AdminListServiceContext.Provider
      value={{
        query,
        collection,
        roles,
        fetchRoles,
        onFetch,
        onFetchV2,
        setCollection,
        updateAdminRole,
        hasNextPage: page.hasNextPage,
        hasPreviousPage: page.hasPrevPage,
      }}
    >
      {children}
    </AdminListServiceContext.Provider>
  );
};
